import {
  ABCErrorMessages,
  FBErrorMessages,
  SMSErrorMessages,
  TwitterErrorMessages,
  WhatsAppErrorMessages,
} from '../constants/common';
import apiErrorTranslations from './apiErrorTranslations';
import chatTranslations from './chatTranslations';

const headerLabels = {
  dmm_title_label: 'DIGITAL MESSAGING MANAGER',
  logout: 'Log out',
  logout_error_message: 'Failed to logout the session',
};

const channelNames = {
  'Web Messaging': 'Web Messaging',
  'Mobile Messaging SDK': 'Mobile Messaging SDK',
  combined_messaging: 'Combined Messaging Channel',
  sdk_ios: 'iOS SDK',
  sdk_android: 'Android SDK',
  sdk_mobile: 'Mobile SDK',
  SMS: 'SMS',
  ABC: 'Apple Messages for Business',
  'Twilio SMS': 'Twilio SMS',
  WhatsApp: 'WhatsApp',
  'Twilio WhatsApp': 'Twilio WhatsApp',
  Facebook: 'Facebook',
  Twitter: 'Twitter',
  'Apple Messages for Business': 'Apple Messages for Business',
  'Client Channel API': 'Client Channel API',
  'Vonage WhatsApp': 'Vonage WhatsApp',
  'Vonage SMS': 'Vonage SMS',
  'GenAI Agent Trainer': 'GenAI Agent Trainer',
};

const addConnectionPageLabels = {
  add_a_connection_label: 'Add a connection',
  add_fb_description: 'Engage through Messenger or public post on pages',
  add_twitter_description: 'Engage through Direct Message or tweeting',
  add_sms_description: 'Add SMS via Twilio',
  add_abc_description: 'Communicate in the Apple Messages App',
  add_sms_description_1: 'Add SMS via Twilio/Vonage',
  add_chat_description: 'Install a chat widget on your website',
  add_sdk_description: 'Enable a messaging SDK for your iOS and Android apps',
  add_combined_messaging_description: 'It enables web messaging channel sharing with Mobile Messaging connection',
  add_whatsapp_description: 'Add WhatsApp via Twilio',
  add_whatsapp_description_1: 'Add WhatsApp via Twilio/Vonage',
  add_api_connection: 'Add Client Channel API',
  add_genAI_agent_trainer: 'Add GenAI Agent Trainer',
};

const modalLabels = {
  delete_modal_confirmation_label: 'Are you sure you want to remove',
  delete_modal_confirmation_label_error_webhook: 'Are you sure you want to remove the webhook mentioned below',
};

const countryCodeLabels = {
  add_country: 'Add country',
  no_country_found: 'No country found',
  allowed_country_codes: 'Allowed country calling codes',
  country_codes_box_info_text:
    'Phone numbers from the following countries and regions have permission to send messages to your application.',
  country_codes_note:
    'You will have to synchronize the geographic permissions for your SMS number in Twilio. In Twilio, search for "SMS Geographic Permissions" and select the countries that you wish to allow inbound messaging.',
  country_code_dropdown_error_info: 'At least one country code needs to be present',
  updated_country_codes: 'Updated country codes successfully',
};

const accountMessages = {
  whatsapp_creation_failure: 'Failed to create Twilio Whatsapp account',
  sms_creation_failure: 'Failed to create Twilio SMS account',
  abc_creation_failure: 'Failed to create ABC account',
  custom_account_creation_failure: 'Failed to create Client Channel API account',
  vonage_whatsapp_creation_failure: 'Failed to create Vonage Whatsapp account',
  vonage_sms_creation_failure: 'Failed to create Vonage SMS account',
  agent_trainer_creation_failure: 'Failed to create GenAI Agent Trainer account',
  menu_configuration_empty: 'Menu reply configuration cannot be empty',
  menu_configuration_validation: '{label} should be present in menu reply configuration',
  menu_configuration_validation_2: '{label} should be present only once in menu reply configuration',
  menu_configuration_validation_3: '{label} and {#} should be present only once in menu reply configuration',
  menu_configuration_validation_4: '{#} should be present only once in menu reply configuration',
  menu_config_update_success: 'Menu reply configuration updated successfully',
  outbound_messaging_enable_success: 'Outbound messaging enabled successfully',
  outbound_messaging_disable_success: 'Outbound messaging disabled successfully',
  menu_config_update_failure: 'Failed to update menu reply configuration',
  menu_config_note:
    'To configure menu replies you can add static text and\n{label} : defines the message that is coming from the Pega platform\n{#} : defines which number menu item was sent',
  menu_preview_note:
    'The output preview displays the menu reply configuration, with placeholder text for {label} coming from Pega platform.\n{label} = I have a billing question',
};

const accountFormLabels = {
  name: 'Name',
  number: 'Number',
  phone_number: 'Phone number',
  account_sid: 'Account SID',
  auth_token: 'Auth token',
  business_chat_id: 'Messages for Business ID',
  show_token: 'Show token',
  hide_token: 'Hide token',
  webhook: 'Webhook',
  url: 'URL',
  client_webhook: 'Client webhook',
  private_key: 'Private key',
  application_id: 'Application Id',
  menu_reply_config: 'Menu reply configuration',
  menu_preview: 'Output preview',
  reset_menu: 'Reset to default',
  twilio_whatsapp_content_template: 'Allow integration with Twilio WhatsApp content templates',
  messaging_service_sid: 'Messaging service Sid',
  messaging_service_sid_empty: 'Messaging service sid cannot be empty',
  enable_outbound_messaging: 'Enable outbound messaging',
  content_template_tooltip:
    'Enabling this configuration defaults the connection to use Content Template quick reply buttons in Twilio WhatsApp',
  messaging_service_sid_tooltip:
    'This value is the Sid of the Messaging Service configured in Twilio for your connection.',
};

const accountFormErrorMessages = {
  name_empty: 'Name cannot be empty',
  auth_token_empty: 'Auth token cannot be empty',
  phone_number_criteria_error: 'Number must be entered with + and can contain only digits',
  phone_number_criteria_error_1: 'Number should not contain alphabets',
  phone_number_criteria_error_2: 'Number should contain only digits',
  account_sid_criteria_error: 'Account SID cannot be empty',
  account_sid_criteria_error_1: 'Account SID should contain only alphabets and digits',
  business_chat_id_criteria_error: 'Messages for Business ID cannot be empty',
  business_chat_id_criteria_error_1: 'Messages for Business ID should contain only alphabets and digits',
  cannot_be_empty: 'cannot be empty',
  command_empty: 'Bot command cannot be empty',
  code_empty: 'Proactive trigger code cannot be empty',
  proactive_trigger_criteria_error: 'Proactive trigger code should contain only alphabets, digits and _',
  proactive_trigger_criteria_error_1: 'Proactive trigger code should be unique',
  client_webhook_criteria_error: 'Client webhook cannot be empty',
  button_text_empty: 'Button text cannot be empty',
  widget_button_max_length: 'Button text should not be more then 15 characters',
  private_key_token_empty: 'Private key cannot be empty',
  application_id_criteria_error: 'Application ID cannot be empty',
  api_key_criteria_error: 'API key cannot be empty',
};

const commonLabels = {
  home: 'Home',
  ok: 'Ok',
  note: 'Note',
  add: 'Add',
  update: 'Update',
  save: 'Save',
  submit: 'Submit',
  new: 'New',
  show: 'Show',
  hide: 'Hide',
  copy: 'Copy',
  edit: 'Edit',
  deleted: 'Delete',
  authorized: 'Authorized',
  authorized_by: 'Authorized By',
  connection: 'Connection',
  connections_heading: 'Connections',
  add_connection_button: 'Add connection',
  account_updated: 'account successfully updated',
  account_deleted: 'account successfully deleted',
  account_registered: 'account successfully registered',
  delete_connection_button: 'Delete connection',
  filter_by_type_label: 'Filter by type',
  invalid_session: 'Invalid or expired session.',
  all_connections: 'All connections',
  last_updated: 'Last updated',
  auth_successful_for: 'Authorization successful for',
  profile_link: 'Profile Link',
  page_link: 'Page Link',
  re_authorize: 'Re-Authorize',
  get_started: 'Messenger Get Started Button',
  add_trigger: 'Add trigger',
  copy_code: 'Copy code snippet',
  third_party: 'Client Channel API',
  start: 'Start',
  stop: 'Stop',
  release_notes: 'Release Notes',
  optional: '(Optional)',
  actions_items_button: 'Action item buttons',
};

const SimulatorLabels = {
  task_empty: 'Simulation scenario cannot be empty',
  queue_name_empty: 'Queue selection cannot be empty',
  updated_simulator_configuration: 'Updated simulation configurations successfully',
  at_least_one_task_should_be_enabled: 'To start simulation at least one scenario should be enabled',
  add_scenarios: 'Add scenarios',
  edit_scenarios: 'Edit scenarios',
  updated_connection_configuration: 'Updated connection and GenAI configurations successfully',
  api_key: 'API key',
  OpenAI: 'OpenAI',
  host: 'Host',
  show_data: 'Show connection details',
  hide_data: 'Hide connection details',
  live_simulation: 'Live Simulation',
  organization_name_empty: 'Organization name cannot be empty',
  escalation_command_empty: 'Escalation command cannot be empty',
  personality_empty: 'Attitude / Personality cannot be empty',
  pain_points_empty: 'Pain points cannot be empty',
  start_simulation: 'Start simulation',
  stop_simulation: 'Stop simulation',
  enable_task_error: 'At least one scenario should be enabled',
  Azure: 'Azure',
  resource: 'Resource',
  oauth_endpoint: 'Azure OAuth endpoint',
  selected_model: 'GenAI model',
  api_version: 'Api version',
  client_id: 'Client id',
  client_secret: 'Client secret',
  oauth_endpoint_error: 'Azure OAuth Endpoint cannot be empty',
  api_key_error: 'Api key cannot be empty',
  resource_error: 'Resource cannot be empty',
  client_id_error: 'Client id cannot be empty',
  client_secret_error: 'Client secret cannot be empty',
  api_version_error: 'Api version cannot be empty',
  simulation_control: 'Simulation Control',
  add_customer: 'Add customer',
  edit_customer: 'Edit customer',
  customer_id: 'Customer id',
  customer_name: 'Customer name',
  full_address: 'Full address',
  date_of_birth: 'Date of birth',
  occupation: 'Occupation',
  accountDetails: 'Account details',
  template: 'Pega application template',
  financial_services: 'Financial services',
  communications: 'Communications',
  insurance: 'Insurance',
  healthcare: 'Health care',
  default: 'Default',
  maximum_messages: 'Maximum messages generated per customer',
  maximum_messages_error: 'Maximum messages generated per customer cannot be empty',
  maximum_messages_error_1: 'Maximum messages generated per customer should not be less then 10',
  maximum_messages_error_2: 'Maximum messages generated per customer should contain only digits',
  genAI_host_option: 'Do you want to add GenAI host now?',
  no: 'No',
  yes: 'Yes',
  genAI_info_banner:
    "There is no GenAI host currently configured. This can be added on the 'Connections and GenAI' tab",
  'Pega GenAI': 'Pega GenAI',
};

const CustomerDetailLabels = {
  state: 'State',
  task: 'Task',
  queue: 'Queue',
  personality: 'Personality',
  phone_number: 'Phone number',
  email: 'Email',
  bio: 'Bio',
  patience_level: 'Patience level',
  show_more: 'Show more',
  back: 'Back',
  transcript: 'Transcript',
  simulation_scenario: 'Simulation Scenario',
  connection_details: 'Connection details',
  gen_ai: 'Gen AI',
  show_messages: 'Show messages',
  occupation_empty: 'Occupation cannot be empty',
  dob_empty: 'Date of birth cannot be empty',
  full_address_empty: 'Full address cannot be empty',
  phone_number_empty: 'Phone number cannot be empty',
  email_empty: 'Email cannot be empty',
  customer_name_empty: 'Customer name cannot be empty',
  updated_customer_details: 'Updated customer details successfully',
  customer_details_failed: 'Failed to update customer details',
  phone_number_validation: 'Please enter a valid phone number',
  accounts_empty: 'Account details cannot be empty',
  delete_persona_success: 'Deleted customer details successfully',
  delete_persona_failed: 'Failed to delete customer details',
  add_persona_success: 'Added customer details successfully',
  add_persona_failed: 'Failed to add customer details',
  edit_persona_success: 'Edited customer details successfully',
  edit_persona_failed: 'Failed to edit customer details',
  customer_id_empty: 'Customer id cannot be empty',
  customer_id_validate: 'Customer id should be unique and can only contain hyphen, alphabets and digits',
  customer_id_max_length: 'Customer id should not be more then 7 characters',
  bulk_upload: 'Bulk upload customers',
  length_exceeded:
    'The total personas are exceeding the maximum limit which is {0}. Kindly remove few customers and upload again.',
  missing_complete_payload: 'Some of the customer details are missing. Kindly ensure all the data is filled properly.',
  custId_unique: 'Customer ID should be unique.',
  download_file: 'Download sample file',
  excel_empty: 'No customer data found.',
  bulk_upload_customer: 'Are you sure you want to upload the following customers?',
};

const SimulationMetricsLabels = {
  simulation_id: 'Simulation id',
  start_time: 'Start time',
  stop_time: 'Stop time',
  simulation_details: 'Simulation details',
  phone_number: 'Phone number',
  email: 'Email',
  bio: 'Bio',
  patience_level: 'Patience level',
  show_more: 'Show more',
  back: 'Back',
  transcript: 'Transcript',
  connection_details: 'Connection details',
  gen_ai: 'Gen AI',
  no_customers_present: 'No customers present',
  empty_simulation_history: 'No simulation history present',
  no_customers_generated: 'No customers generated yet',
  download_csv_file: 'Download csv file',
};

const AgentFeedbackLabels = {
  feedback_language: 'Feedback language',
  send_agent_feedback: 'Send feedback to agent',
  title: 'Title',
  description: 'Description',
  title_empty: 'Title cannot be empty',
  description_empty: 'Description cannot be empty',
  add_feedback: 'Add feedback item',
  edit_feedback: 'Edit feedback item',
  feedback_lang_selection: 'Select a language',
  response_type: 'Response type',
  enable_agent_feedback_success: 'Updated send feedback to agent successfully',
  enable_agent_feedback_failure: 'Failed to update send feedback to agent',
  add_feedback_success: 'Added feedback configuration successfully',
  add_feedback_failed: 'Failed to add feedback configuration',
  edit_feedback_success: 'Updated feedback configuration successfully',
  edit_feedback_failed: 'Failed to update feedback configuration',
  delete_feedback_success: 'Deleted feedback configuration successfully',
  delete_feedback_failed: 'Failed to delete feedback configuration',
  feedback_language_success: 'Updated feedback language successfully',
  feedback_language_failed: 'Failed to updated feedback language',
  feedback_webhook: 'Add webhook endpoint',
  feedback_webhook_payload_header: 'Feedback payload',
  edit_feedback_webhook: 'Edit feedback webhook',
  add_webhook_success: 'Added feedback webhook successfully',
  add_webhook_failed: 'Failed to add feedback webhook',
  edit_webhook_success: 'Updated feedback webhook successfully',
  edit_webhook_failed: 'Failed to update feedback webhook',
  delete_webhook_success: 'Deleted feedback webhook successfully',
  delete_webhook_failed: 'Failed to delete feedback webhook',
  feedback_webhook_payload_text:
    'The payload below will be sent to your configured webhooks once feedback is created during a simulation. These fields are based on feedback item configuration.',
};

const ErrorLogLabels = {
  additional_info: 'Click the icon to see info',
  close: 'Close',
  error_logs: 'Error logs',
  col_definition_info: 'Column definitions for Digital Messaging error logs are provided below',
  timestamp_info: 'Date and time of log entry.',
  reason_info: 'Reason for failure to reach messaging endpoint.',
  http_status_info: 'Error code returned by messaging endpoint if available.',
  attempt_info:
    'Initial attempt or re-try. Digital Messaging attempts to re-send twice in case of failure, at 30sec intervals and stops attempts after a third failure.',
  queue_time_info:
    'Time (in seconds) spent within Digital Messaging Service before message delivery attempt to messaging endpoint.',
  duration_info: 'Time (in seconds) for messaging endpoint to return a response. Timeout period is 30 seconds.',
  connection_id_info: 'ID of the connection (e.g. Web Messaging, WhatsApp etc) configured in the channel interface.',
  connection_name_info:
    'Name of the connection (e.g. Web Messaging, WhatsApp etc) configured in the channel interface.',
  webhook_info:
    'Messaging endpoint that Digital Messaging Service attempts delivery to. URL is formed by Base URL field value in channel interface, and system appends "/PRRestService/botAgent/v1/messaging". This messaging endpoint must be secure (https) and publicly accessible off VPN.',
  manager_id_info:
    'Digital Messaging Manager ID configured in application definition security for the channel interface.',
  consumer_id_info: 'ID of the channel interface where the Digital Messaging connections are configured.',
  device_info_info: 'Device type and version provided with message if available.',
  requestId_info: 'ID of the message group in Digital Messaging logs.',
  error_docs: 'Digital Messaging Manager error logging dashboard',
  dashboard_info_text:
    'The Digital Messaging Manager (DMM) Error Logging Dashboard provides a user-friendly interface for monitoring, tracking and responding to error occurring within Digital Messaging Service (DMS). Only failures on attempts to forward inbound messages from the customer to messaging endpoint and included. Outbound failures on replies and inbound failures from the customer to Digital Messaging Service and not displayed.',
};

const ConfigureTabLabels = {
  configuration_type: 'Configuration type',
  language_service: 'Google translate language service',
  successfully_created: 'successfully created',
  successfully_deleted: 'successfully deleted',
  lang_service_creation_failure: 'Failed to create Language Service account',
  failed_to_get_language_service: 'Failed to get Language Service account details.',
  failed_to_remove_language_service: 'Failed to remove Language Service account.',
  lang_config_update_failure: 'Failed to update api key',
  lang_config_update_success: 'Successfully updated api key',
  add_new: 'Add new',
  global_config: 'Global configurations',
  add_global_config: 'Add new global configuration',
  disable_delete_info: "Language service configurations can't be deleted while being used in active connections",
};

// default language/translation used if no locale version provided
export const defaultTranslations: any = {
  ...commonLabels,
  ...channelNames,
  ...headerLabels,
  ...addConnectionPageLabels,
  ...modalLabels,
  ...countryCodeLabels,
  ...accountMessages,
  ...accountFormLabels,
  ...accountFormErrorMessages,
  ...TwitterErrorMessages,
  ...WhatsAppErrorMessages,
  ...SMSErrorMessages,
  ...FBErrorMessages,
  ...ABCErrorMessages,
  ...chatTranslations,
  ...apiErrorTranslations,
  ...SimulatorLabels,
  ...CustomerDetailLabels,
  ...SimulationMetricsLabels,
  ...AgentFeedbackLabels,
  ...ErrorLogLabels,
  ...ConfigureTabLabels,
};
